import React, { useState, useEffect } from 'react';
import {
  AppBar,
  Toolbar,
  Typography,
  Box,
  Drawer,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  IconButton,
  Avatar,
  useMediaQuery,
  useTheme,
  Tooltip,
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import CloseIcon from '@mui/icons-material/Close';
import LogoutIcon from '@mui/icons-material/Logout';
import DashboardIcon from '@mui/icons-material/Dashboard';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import PaymentIcon from '@mui/icons-material/Payment';
import PersonIcon from '@mui/icons-material/Person';
import LocalHospitalIcon from '@mui/icons-material/LocalHospital';
import BrandMark from '../components/BrandMark';
import MedicalServicesIcon from '@mui/icons-material/MedicalServices';
import ApartmentIcon from '@mui/icons-material/Apartment';
import ScheduleIcon from '@mui/icons-material/Schedule';
import QueueIcon from '@mui/icons-material/FormatListNumbered';
import SendIcon from '@mui/icons-material/Send';
import LocalPharmacyIcon from '@mui/icons-material/LocalPharmacy';
import BiotechIcon from '@mui/icons-material/Biotech';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import EventNoteIcon from '@mui/icons-material/EventNote';
import MonetizationOnIcon from '@mui/icons-material/MonetizationOn';
import PeopleIcon from '@mui/icons-material/People';
import SettingsIcon from '@mui/icons-material/Settings';
import HealthAndSafetyIcon from '@mui/icons-material/HealthAndSafety';
import BusinessIcon from '@mui/icons-material/Business';
import MonitorHeartIcon from '@mui/icons-material/MonitorHeart';
import BarChartIcon from '@mui/icons-material/BarChart';
import NotificationsIcon from '@mui/icons-material/Notifications';
import ChatIcon from '@mui/icons-material/Chat';
import HistoryIcon from '@mui/icons-material/History';
import DescriptionIcon from '@mui/icons-material/Description';
import GroupsIcon from '@mui/icons-material/Groups';
import CallSplitIcon from '@mui/icons-material/CallSplit';
import StorefrontIcon from '@mui/icons-material/Storefront';
import LiveTvIcon from '@mui/icons-material/LiveTv';
import HandshakeIcon from '@mui/icons-material/Handshake';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';
import InboxRoundedIcon from '@mui/icons-material/InboxRounded';
import Badge from '@mui/material/Badge';
import Chip from '@mui/material/Chip';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import useAuth from '../hooks/useAuth';
import { logout } from '../store/slices/authSlice';
import { getInitials } from '../utils/helpers';
import { labService, notificationService } from '../api/services';
import FontSizeControl from '../components/FontSizeControl';
import LanguageSwitcher from '../components/LanguageSwitcher';
import LabAlertsBell from '../components/lab/LabAlertsBell';
import { LAB_ROLES } from '../utils/constants';
import { setLabHospital } from '../utils/labHospital';
import { labNavFor, hospitalAdminLabNav } from './labNav';
import {
  BRAND,
  OCEAN,
  EDGE,
  RAIL,
  APPBAR_BG,
  HEADER_GRADIENT,
  keyframe,
  gradientText,
  reduceMotion,
} from '../theme/oceanTokens';

const DRAWER_WIDTH = 264;

/** Groups nav items by `group`, in order of first appearance. */
const groupNav = (items) => {
  const order = [];
  const byGroup = {};
  items.forEach((item) => {
    const g = item.group || 'Menu';
    if (!byGroup[g]) {
      byGroup[g] = [];
      order.push(g);
    }
    byGroup[g].push(item);
  });
  return order.map((g) => ({ group: g, items: byGroup[g] }));
};

const CUSTOMER_NAV = [
  { label: 'Dashboard', path: '/customer/dashboard', icon: DashboardIcon, group: 'Overview' },
  { label: 'My Appointments', path: '/customer/appointments', icon: CalendarMonthIcon, group: 'Care' },
  { label: 'Prescriptions', path: '/customer/prescriptions', icon: DescriptionIcon, group: 'Care' },
  { label: 'Lab Reports', path: '/customer/lab-reports', icon: BiotechIcon, group: 'Care' },
  { label: 'Invoices', path: '/customer/invoices', icon: MonetizationOnIcon, group: 'Billing' },
  { label: 'Payment History', path: '/customer/payments', icon: PaymentIcon, group: 'Billing' },
  { label: 'My Feedback', path: '/customer/feedback', icon: GroupsIcon, group: 'Account' },
  { label: 'Notifications', path: '/customer/notifications', icon: NotificationsIcon, group: 'Account' },
  { label: 'Profile', path: '/customer/profile', icon: PersonIcon, group: 'Account' },
];

const DOCTOR_NAV = [
  { label: 'Dashboard', path: '/doctor/dashboard', icon: DashboardIcon, group: 'Overview' },
  { label: 'My Appointments', path: '/doctor/appointments', icon: CalendarMonthIcon, group: 'Today' },
  { label: 'Queue', path: '/doctor/queue', icon: QueueIcon, group: 'Today' },
  { label: 'Manage Schedule', path: '/doctor/slots', icon: ScheduleIcon, group: 'Today' },
  { label: 'Surgery Referrals', path: '/doctor/surgery-referrals', icon: LocalHospitalIcon, group: 'Referrals' },
  { label: 'External Referrals', path: '/doctor/external-referrals', icon: CallSplitIcon, group: 'Referrals' },
  { label: 'Pharmacy', path: '/doctor/pharmacy', icon: LocalPharmacyIcon, group: 'Orders' },
  { label: 'Lab Tests', path: '/doctor/lab-tests', icon: BiotechIcon, group: 'Orders' },
  { label: 'Lab Orders', path: '/doctor/lab-orders', icon: BiotechIcon, group: 'Orders', labOnly: true },
  { label: 'Follow-ups', path: '/doctor/follow-ups', icon: NotificationsIcon, group: 'Care' },
  { label: 'Workflows', path: '/doctor/workflows', icon: MonitorHeartIcon, group: 'Care' },
  { label: 'Messages', path: '/doctor/messages', icon: ChatIcon, group: 'Care' },
  { label: 'Payments', path: '/doctor/payments', icon: MonetizationOnIcon, group: 'Account' },
  { label: 'Referrals', path: '/doctor/referrals', icon: SendIcon, group: 'Referrals' },
  { label: 'Profile', path: '/doctor/profile', icon: PersonIcon, group: 'Account' },
];

const HOSPITAL_NAV = [
  { label: 'Dashboard', path: '/hospital/dashboard', icon: DashboardIcon, group: 'Overview' },
  { label: 'Hospital Profile', path: '/hospital/profile', icon: BusinessIcon, group: 'Overview' },
  { label: 'Doctors', path: '/hospital/doctors', icon: MedicalServicesIcon, group: 'People', countKey: 'total_doctors' },
  { label: 'Branches', path: '/hospital/branches', icon: ApartmentIcon, group: 'People', countKey: 'active_branches' },
  { label: 'Availability', path: '/hospital/availability', icon: ScheduleIcon, group: 'Scheduling' },
  { label: 'Doctor Status', path: '/hospital/doctor-status', icon: MonitorHeartIcon, group: 'Scheduling' },
  { label: 'Appointments', path: '/hospital/appointments', icon: EventNoteIcon, group: 'Scheduling', countKey: 'today_appointment_count' },
  { label: 'Follow-ups', path: '/hospital/follow-ups', icon: NotificationsActiveIcon, group: 'Scheduling' },
  { label: 'Invoices', path: '/hospital/invoices', icon: MonetizationOnIcon, group: 'Money' },
  { label: 'Partners & Clients', path: '/hospital/partners', icon: HandshakeIcon, group: 'People' },
  { label: 'Coupons & Offers', path: '/hospital/coupons', icon: LocalOfferIcon, group: 'Money' },
  { label: 'Payment Gateway', path: '/hospital/payment-gateway', icon: SettingsIcon, group: 'Money' },
  { label: 'Insurance', path: '/hospital/insurance', icon: HealthAndSafetyIcon, group: 'Money' },
  { label: 'Feedback', path: '/hospital/feedback', icon: GroupsIcon, group: 'More' },
  { label: 'Live Queue (Kiosk)', path: '/queue/kiosk', icon: LiveTvIcon, external: true, group: 'Scheduling' },
  { label: 'Customers', path: '/hospital/customers', icon: PeopleIcon, group: 'People' },
  { label: 'Messages', path: '/hospital/messages', icon: ChatIcon, group: 'More' },
  { label: 'Message Logs', path: '/hospital/communications', icon: HistoryIcon, group: 'More' },
  { label: 'Counseling', path: '/hospital/counseling', icon: GroupsIcon, group: 'More' },
  { label: 'Clinic Services', path: '/hospital/services-clinic', icon: StorefrontIcon, group: 'Services' },
  { label: 'Pharmacy', path: '/hospital/pharmacy', icon: LocalPharmacyIcon, group: 'Services' },
  { label: 'Diagnostics', path: '/hospital/diagnostics', icon: BiotechIcon, group: 'Services' },
  { label: 'Revenue', path: '/hospital/revenue', icon: MonetizationOnIcon, group: 'Money' },
  { label: 'Knowledge Base', path: '/hospital/knowledge', icon: MenuBookIcon, group: 'More' },
];

const ADMIN_NAV = [
  { label: 'Dashboard', path: '/admin/dashboard', icon: DashboardIcon, group: 'Overview' },
  { label: 'Hospitals', path: '/admin/hospitals', icon: BusinessIcon, group: 'Directory' },
  { label: 'Doctors', path: '/admin/doctors', icon: MedicalServicesIcon, group: 'Directory' },
  { label: 'Customers', path: '/admin/customers', icon: PeopleIcon, group: 'Directory' },
  { label: 'Appointments', path: '/admin/appointments', icon: MonitorHeartIcon, group: 'Operations' },
  { label: 'Revenue', path: '/admin/revenue', icon: BarChartIcon, group: 'Operations' },
  { label: 'Laboratories', path: '/admin/labs', icon: BiotechIcon, group: 'Operations' },
  { label: 'Medicines', path: '/admin/medicines', icon: LocalPharmacyIcon, group: 'Catalogue' },
  { label: 'Prescription Packs', path: '/admin/medicine-packs', icon: LocalPharmacyIcon, group: 'Catalogue' },
  { label: 'Tests', path: '/admin/tests', icon: BiotechIcon, group: 'Catalogue' },
  { label: 'Test Panels', path: '/admin/test-panels', icon: BiotechIcon, group: 'Catalogue' },
  { label: 'Knowledge Base', path: '/admin/knowledge', icon: MenuBookIcon, group: 'System' },
  { label: 'Settings', path: '/admin/settings', icon: SettingsIcon, group: 'System' },
];

const DashboardLayout = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('lg'));
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const { user, role, can, labEnabled, labRole } = useAuth();
  const { clinics, selectedClinic } = useSelector((state) => state.auth);
  const hospitalStats = useSelector((state) => state.hospitals?.dashboard) || {};

  const [mobileOpen, setMobileOpen] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);

  // Every role has notifications: doctors get lab results and critical values,
  // lab staff get new orders and bills awaiting approval.
  const notificationsPath = role === 'customer' ? '/customer/notifications' : '/notifications';
  useEffect(() => {
    if (!role) return;
    notificationService
      .getNotifications()
      .then((res) => {
        const notifs = res.data?.results || res.data || [];
        setUnreadCount(notifs.filter((n) => !n.is_read).length);
      })
      .catch(() => {});
  }, [role, location.pathname]);

  // Lab alerts bell and sidebar counts (06 §2.3–2.4): on /lab pages, fetched on mount and on every
  // route change — never on a timer (D14).
  const showLabBell = location.pathname.startsWith('/lab') && (role === 'lab_staff' || (role === 'hospital_admin' && labEnabled));
  const [labAlerts, setLabAlerts] = useState({ items: [], counts: {} });
  useEffect(() => {
    if (!showLabBell) return;
    labService
      .getAlerts()
      .then((res) => setLabAlerts({ items: res.data?.items || [], counts: res.data?.counts || {} }))
      .catch(() => {});
  }, [showLabBell, location.pathname]);

  const handleDrawerToggle = () => setMobileOpen(!mobileOpen);

  const navItems =
    role === 'admin'
      ? ADMIN_NAV
      : role === 'hospital_admin'
      ? [...HOSPITAL_NAV, ...hospitalAdminLabNav(labEnabled)]
      : role === 'doctor'
      ? DOCTOR_NAV.filter((i) => !i.labOnly || labEnabled)
      : role === 'lab_staff'
      ? labNavFor(can, labRole)
      : CUSTOMER_NAV;

  const roleLabel =
    role === 'admin'
      ? 'Platform Admin'
      : role === 'hospital_admin'
      ? 'Hospital Admin'
      : role === 'doctor'
      ? 'Doctor'
      : role === 'lab_staff'
      ? LAB_ROLES.find((r) => r.value === labRole)?.label || 'Lab Staff'
      : 'Customer';

  const displayName =
    user?.first_name || user?.last_name
      ? `${user?.first_name || ''} ${user?.last_name || ''}`.trim()
      : user?.name || 'User';

  // The seeded platform admin is literally named "Platform Admin", so the role
  // line would just repeat the name. Drop it when it adds nothing.
  const showRole = roleLabel.toLowerCase() !== displayName.toLowerCase();

  const currentPageTitle =
    navItems.find(
      (item) =>
        location.pathname === item.path ||
        location.pathname.startsWith(item.path + '/')
    )?.label || 'Dashboard';

  const handleLogout = () => {
    setLabHospital(null);
    dispatch(logout());
    navigate('/login');
  };

  const isActive = (path) =>
    location.pathname === path || location.pathname.startsWith(path + '/');

  const navCount = (item) => {
    if (item.countKey && item.path.startsWith('/lab/')) return labAlerts.counts[item.countKey];
    if (item.countKey && role === 'hospital_admin') return hospitalStats[item.countKey];
    if (item.path === '/customer/notifications' && unreadCount > 0) return unreadCount;
    return undefined;
  };

  const drawerContent = (
    <Box
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        background: RAIL.bg,
        color: RAIL.text,
      }}
    >
      {/* Brand */}
      <Box
        sx={{
          px: 2,
          pt: 2.25,
          pb: 1.75,
          display: 'flex',
          alignItems: 'center',
          gap: 1.25,
          cursor: 'pointer',
        }}
        onClick={() => navigate('/')}
      >
        <BrandMark size={36} />
        <Typography
          variant="h5"
          sx={{
            fontWeight: 800,
            color: RAIL.brandText,
            letterSpacing: '-0.01em',
            fontSize: '1.15rem',
          }}
        >
          PyMedic
        </Typography>
        {isMobile && (
          <IconButton
            onClick={handleDrawerToggle}
            sx={{ ml: 'auto', color: RAIL.text }}
            size="small"
          >
            <CloseIcon />
          </IconButton>
        )}
      </Box>

      {/* Who */}
      <Box sx={{ px: 1.5, pb: 1.25 }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1.25,
            p: 1.25,
            borderRadius: '12px',
            bgcolor: RAIL.who,
          }}
        >
          <Avatar
            src={user?.profile_photo}
            sx={{
              width: 34,
              height: 34,
              fontSize: '0.8rem',
              fontWeight: 700,
              color: '#fff',
              background: `linear-gradient(140deg, ${BRAND.c1}, ${OCEAN.aqua})`,
            }}
          >
            {getInitials(displayName)}
          </Avatar>
          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Typography
              variant="body2"
              fontWeight={600}
              noWrap
              sx={{ color: RAIL.whoName, fontSize: '0.8rem' }}
            >
              {displayName}
            </Typography>
            <Typography
              variant="caption"
              sx={{ fontSize: '0.68rem', color: RAIL.whoRole }}
            >
              {roleLabel}
            </Typography>
          </Box>
        </Box>
      </Box>

      {/* Menu */}
      <List
        sx={{
          flex: 1,
          px: 1.5,
          py: 0,
          overflowY: 'auto',
          scrollbarWidth: 'thin',
          '&::-webkit-scrollbar-track': { background: 'transparent' },
          '&::-webkit-scrollbar-thumb': { background: RAIL.scrollThumb },
        }}
      >
        {groupNav(navItems).map(({ group, items }) => (
          <React.Fragment key={group}>
            <Typography
              sx={{
                px: 1.4,
                pt: 1.6,
                pb: 0.6,
                fontSize: '0.6rem',
                fontWeight: 700,
                letterSpacing: '0.14em',
                textTransform: 'uppercase',
                color: RAIL.group,
              }}
            >
              {group}
            </Typography>
            {items.map((item) => {
              const Icon = item.icon;
              const active = isActive(item.path) && !item.external;
              const count = navCount(item);
              return (
                <ListItemButton
                  key={item.path}
                  selected={active}
                  onClick={() => {
                    if (item.external) {
                      window.open(item.path, '_blank', 'noopener,noreferrer');
                    } else {
                      navigate(item.path);
                    }
                    if (isMobile) handleDrawerToggle();
                  }}
                  sx={{
                    mb: 0.25,
                    borderRadius: '9px',
                    py: 0.95,
                    px: 1.4,
                    color: RAIL.text,
                    position: 'relative',
                    transition: 'background .2s, color .2s, padding-left .2s',
                    '&:hover': {
                      bgcolor: RAIL.hover,
                      color: RAIL.textHover,
                      pl: 1.8,
                    },
                    '&.Mui-selected': {
                      color: RAIL.activeText,
                      background: RAIL.active,
                      boxShadow: RAIL.activeGlow,
                      '&:hover': { background: RAIL.active, pl: 1.4 },
                      // Bar on the rail's left edge.
                      '&::before': {
                        content: '""',
                        position: 'absolute',
                        left: -12,
                        top: 7,
                        bottom: 7,
                        width: 3,
                        borderRadius: '0 3px 3px 0',
                        bgcolor: RAIL.edgeBar,
                      },
                    },
                    [reduceMotion]: { transition: 'none' },
                  }}
                >
                  <ListItemIcon sx={{ minWidth: 34, color: 'inherit' }}>
                    <Icon sx={{ fontSize: 19 }} />
                  </ListItemIcon>
                  <ListItemText
                    primary={item.label}
                    primaryTypographyProps={{
                      fontWeight: active ? 600 : 500,
                      fontSize: '0.83rem',
                      noWrap: true,
                    }}
                  />
                  {count !== undefined && count !== null && (
                    <Box
                      component="span"
                      sx={{
                        ml: 1,
                        fontSize: '0.66rem',
                        fontWeight: 600,
                        fontVariantNumeric: 'tabular-nums',
                        borderRadius: 99,
                        px: 0.9,
                        py: 0.1,
                        bgcolor: active ? 'rgba(255,255,255,0.26)' : RAIL.count,
                        color: active ? '#fff' : RAIL.countText,
                        flex: 'none',
                      }}
                    >
                      {count}
                    </Box>
                  )}
                </ListItemButton>
              );
            })}
          </React.Fragment>
        ))}
      </List>
    </Box>
  );

  return (
    <Box
      sx={{
        display: 'flex',
        minHeight: '100vh',
        bgcolor: 'background.default',
        '@media print': { display: 'block', minHeight: 0 },
      }}
    >
      {/* Permanent drawer for desktop */}
      {!isMobile && (
        <Drawer
          variant="permanent"
          sx={{
            width: DRAWER_WIDTH,
            flexShrink: 0,
            '@media print': { display: 'none' },
            '& .MuiDrawer-paper': {
              width: DRAWER_WIDTH,
              boxSizing: 'border-box',
              border: 0,
              borderRadius: 0,
            },
          }}
        >
          {drawerContent}
        </Drawer>
      )}

      {/* Temporary drawer for mobile */}
      {isMobile && (
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{ keepMounted: true }}
          sx={{
            '& .MuiDrawer-paper': {
              width: DRAWER_WIDTH,
              border: 0,
              borderRadius: '0 16px 16px 0',
            },
          }}
        >
          {drawerContent}
        </Drawer>
      )}

      {/* Main content */}
      <Box
        sx={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          minWidth: 0,
        }}
      >
        <AppBar
          position="sticky"
          sx={{
            bgcolor: APPBAR_BG,
            backdropFilter: 'blur(8px)',
            borderBottom: '1px solid',
            borderColor: 'divider',
            '@media print': { display: 'none' },
          }}
        >
          <Toolbar sx={{ minHeight: { xs: 56, sm: 64 }, px: { xs: 1.5, sm: 3 } }}>
            {isMobile && (
              <IconButton
                edge="start"
                onClick={handleDrawerToggle}
                sx={{ mr: { xs: 1, sm: 1.5 }, p: { xs: 0.75, sm: 1 } }}
                size="small"
                aria-label="open menu"
              >
                <MenuIcon sx={{ fontSize: { xs: 22, sm: 24 } }} />
              </IconButton>
            )}
            <Typography
              variant="h6"
              fontWeight={800}
              noWrap
              sx={{
                ...gradientText,
                flex: 1,
                fontSize: { xs: '1rem', sm: '1.25rem' },
                mr: 1,
              }}
            >
              {currentPageTitle}
            </Typography>

            <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 0.75, sm: 1.5 }, flexShrink: 0 }}>
              {/* The clinic is deliberately cleared on every login, so a doctor
                  who deep-links straight to a page has none selected. Showing
                  the chip regardless - as a prompt when nothing is chosen -
                  keeps a way back to the picker instead of stranding them. */}
              {role === 'doctor' && (clinics?.length || 0) > 0 && (
                <Tooltip title={selectedClinic ? 'Switch clinic' : 'Choose your clinic'}>
                  <Chip
                    icon={<SwapHorizIcon />}
                    label={selectedClinic ? selectedClinic.name : 'Select clinic'}
                    variant={selectedClinic ? 'outlined' : 'filled'}
                    color={selectedClinic ? 'primary' : 'warning'}
                    onClick={() => navigate('/doctor/select-clinic')}
                    sx={{ fontWeight: 600, maxWidth: 220 }}
                  />
                </Tooltip>
              )}
              <Box sx={{ display: { xs: 'none', sm: 'flex' } }}>
                <FontSizeControl />
              </Box>
              <LanguageSwitcher />
              {showLabBell && <LabAlertsBell items={labAlerts.items} />}
              {role && (
                <Tooltip title="Notifications">
                  <IconButton
                    size="small"
                    aria-label="Notifications"
                    onClick={() => navigate(notificationsPath)}
                  >
                    <Badge badgeContent={unreadCount} color="error" max={99}>
                      {/* Beside the lab Alerts bell, an inbox keeps the two apart. */}
                      {showLabBell ? (
                        <InboxRoundedIcon sx={{ fontSize: 22, color: 'text.secondary' }} />
                      ) : (
                        <NotificationsIcon sx={{ fontSize: 22, color: 'text.secondary' }} />
                      )}
                    </Badge>
                  </IconButton>
                </Tooltip>
              )}
              {/* Identity chip: gradient avatar + name, lifts on hover. */}
              <Box
                sx={{
                  display: { xs: 'none', sm: 'flex' },
                  alignItems: 'center',
                  gap: 1,
                  pl: 0.5,
                  pr: 1.25,
                  py: 0.4,
                  borderRadius: 99,
                  border: `1px solid ${EDGE}`,
                  background:
                    'linear-gradient(135deg, rgb(var(--ocean-accent-rgb) / 0.05), rgb(var(--ocean-accent2-rgb) / 0.07))',
                  transition: 'border-color .25s, box-shadow .25s, transform .2s',
                  animation: `${keyframe.fadeDown} .5s ease backwards`,
                  '&:hover': {
                    borderColor: 'rgb(var(--ocean-accent-rgb) / 0.35)',
                    boxShadow: '0 4px 14px rgb(var(--ocean-accent-rgb) / 0.12)',
                    transform: 'translateY(-1px)',
                  },
                  '&:hover .user-avatar': { transform: 'scale(1.08) rotate(-6deg)' },
                  [reduceMotion]: {
                    transition: 'none',
                    animation: 'none',
                    '&:hover': { transform: 'none' },
                  },
                }}
              >
                <Avatar
                  className="user-avatar"
                  src={user?.profile_photo}
                  sx={{
                    width: 30,
                    height: 30,
                    fontSize: '0.72rem',
                    fontWeight: 700,
                    color: '#fff',
                    background: HEADER_GRADIENT,
                    backgroundSize: '200% 100%',
                    transition: 'transform .3s cubic-bezier(.2,.8,.3,1)',
                    [reduceMotion]: { transition: 'none' },
                  }}
                >
                  {getInitials(displayName)}
                </Avatar>
                <Box sx={{ minWidth: 0, textAlign: 'left' }}>
                  <Typography
                    noWrap
                    sx={{
                      ...gradientText,
                      fontSize: '0.8rem',
                      fontWeight: 700,
                      lineHeight: 1.25,
                      maxWidth: 160,
                    }}
                  >
                    {displayName}
                  </Typography>
                  {showRole && (
                    <Typography
                      noWrap
                      sx={{ fontSize: '0.68rem', color: 'text.secondary', lineHeight: 1.25 }}
                    >
                      {roleLabel}
                    </Typography>
                  )}
                </Box>
              </Box>
              <Tooltip title="Logout">
                <IconButton
                  onClick={handleLogout}
                  size="small"
                  aria-label="Logout"
                  sx={{
                    width: 34,
                    height: 34,
                    color: 'text.secondary',
                    transition:
                      'transform .22s cubic-bezier(.2,.8,.3,1), background .25s, color .2s, box-shadow .25s',
                    '& .logout-glyph': { transition: 'transform .25s' },
                    '&:hover': {
                      background: 'linear-gradient(135deg, #991B1B, #EF4444)',
                      color: '#fff',
                      transform: 'scale(1.08)',
                      boxShadow: '0 4px 14px rgba(185,28,28,0.32)',
                    },
                    // The glyph steps through the doorway.
                    '&:hover .logout-glyph': { transform: 'translateX(2px)' },
                    '&:active': { transform: 'scale(0.95)' },
                    [reduceMotion]: {
                      transition: 'none',
                      '& .logout-glyph': { transition: 'none' },
                      '&:hover': { transform: 'none' },
                    },
                  }}
                >
                  <LogoutIcon className="logout-glyph" sx={{ fontSize: 19 }} />
                </IconButton>
              </Tooltip>
            </Box>
          </Toolbar>
        </AppBar>

        <Box
          component="main"
          sx={{
            flex: 1,
            p: { xs: 2, sm: 3 },
            overflow: 'auto',
            // On paper the page is the only thing left, so it takes the whole
            // sheet rather than sitting in the column the sidebar left behind.
            '@media print': { p: 0, overflow: 'visible' },
          }}
        >
          <Outlet />
        </Box>
      </Box>
    </Box>
  );
};

export default DashboardLayout;
